import { useState, useEffect, useMemo } from 'react';
import { Button, Input, Modal, Table, Card, StatsCard, useToast } from '../components/common';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface ApiKeyWithStats {
  id: number;
  user_id: number;
  user_email?: string;
  user_username?: string;
  key_value: string;
  description: string | null;
  is_active: boolean;
  total_requests: number;
  total_tokens: number;
  total_traffic_bytes: number;
  created_at: string;
  last_used_at: string | null;
}

interface ApiKeyStats {
  total_tokens: number;
  total_traffic_bytes: number;
}

type StatusFilter = 'all' | 'active' | 'inactive';

export default function ApiKeysPage() {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [apiKeys, setApiKeys] = useState<ApiKeyWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKeyWithStats | null>(null);
  const [description, setDescription] = useState('');
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null);
  const [totalStats, setTotalStats] = useState<ApiKeyStats>({ total_tokens: 0, total_traffic_bytes: 0 });
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  
  const isAdmin = user?.is_superuser;

  useEffect(() => { fetchApiKeys(); }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await api.get<ApiKeyWithStats[]>('/api/v1/api-keys');
      setApiKeys(response.data);
      
      // Calculate totals
      const totals = response.data.reduce((acc, key) => ({
        total_tokens: acc.total_tokens + (key.total_tokens || 0),
        total_traffic_bytes: acc.total_traffic_bytes + (key.total_traffic_bytes || 0),
      }), { total_tokens: 0, total_traffic_bytes: 0 });
      setTotalStats(totals);
    } catch (error) {
      addToast('error', '無法載入 API Key 列表');
    } finally {
      setLoading(false);
    }
  };

  // Filtered API keys
  const filteredApiKeys = useMemo(() => {
    return apiKeys.filter(key => {
      // Search filter (key value, description, user info)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchKey = key.key_value.toLowerCase().includes(query);
        const matchDesc = key.description?.toLowerCase().includes(query);
        const matchUser = key.user_username?.toLowerCase().includes(query) || 
                         key.user_email?.toLowerCase().includes(query);
        if (!matchKey && !matchDesc && !matchUser) {
          return false;
        }
      }
      // Status filter
      if (statusFilter === 'active' && !key.is_active) return false;
      if (statusFilter === 'inactive' && key.is_active) return false;
      return true;
    });
  }, [apiKeys, searchQuery, statusFilter]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post<ApiKeyWithStats>('/api/v1/api-keys', { description });
      setNewKeyValue(response.data.key_value);
      addToast('success', 'API Key 已建立');
      setDescription('');
      fetchApiKeys();
    } catch (error: any) {
      addToast('error', error.response?.data?.error?.message || '建立失敗');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingKey) return;
    try {
      await api.put(`/api/v1/api-keys/${editingKey.id}`, { description });
      addToast('success', 'API Key 已更新');
      setShowModal(false);
      setEditingKey(null);
      fetchApiKeys();
    } catch (error: any) {
      addToast('error', error.response?.data?.error?.message || '更新失敗');
    }
  };

  const handleToggleStatus = async (key: ApiKeyWithStats) => {
    try {
      await api.put(`/api/v1/api-keys/${key.id}`, { is_active: !key.is_active });
      addToast('success', `API Key 已${key.is_active ? '停用' : '啟用'}`);
      fetchApiKeys();
    } catch (error: any) {
      addToast('error', error.response?.data?.error?.message || '操作失敗');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    addToast('success', '已複製到剪貼簿');
  };

  const openEditModal = (key: ApiKeyWithStats) => {
    setEditingKey(key);
    setDescription(key.description || '');
    setShowModal(true);
  };

  const maskKey = (key: string) => key.substring(0, 8) + '...' + key.substring(key.length - 4);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTokens = (tokens: number) => {
    if (tokens >= 1000000) return (tokens / 1000000).toFixed(2) + 'M';
    if (tokens >= 1000) return (tokens / 1000).toFixed(1) + 'K';
    return tokens.toString();
  };

  const columns = [
    // Show user info column only for admin
    ...(isAdmin ? [{
      key: 'user_info',
      header: '用戶',
      render: (k: ApiKeyWithStats) => (
        <div className="text-sm">
          <div className="font-medium text-gray-900 dark:text-white">{k.user_username || '-'}</div>
          <div className="text-gray-500 dark:text-gray-400 text-xs">{k.user_email || '-'}</div>
        </div>
      )
    }] : []),
    { key: 'key_value', header: 'API Key', render: (k: ApiKeyWithStats) => (
      <div className="flex items-center gap-2">
        <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{maskKey(k.key_value)}</code>
        <button onClick={() => copyToClipboard(k.key_value)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" title="複製">📋</button>
      </div>
    )},
    { key: 'description', header: '描述', render: (k: ApiKeyWithStats) => k.description || '-' },
    { key: 'total_requests', header: '使用次數' },
    { key: 'total_tokens', header: 'Tokens', render: (k: ApiKeyWithStats) => formatTokens(k.total_tokens || 0) },
    { key: 'total_traffic_bytes', header: '流量', render: (k: ApiKeyWithStats) => formatBytes(k.total_traffic_bytes || 0) },
    { key: 'is_active', header: '狀態', render: (k: ApiKeyWithStats) => (
      <span className={`px-2 py-1 rounded text-xs ${k.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
        {k.is_active ? '啟用' : '停用'}
      </span>
    )},
    { key: 'last_used_at', header: '最後使用', render: (k: ApiKeyWithStats) => k.last_used_at ? new Date(k.last_used_at).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei', hour12: false }) : '-' },
    { key: 'created_at', header: '建立時間', render: (k: ApiKeyWithStats) => new Date(k.created_at).toLocaleDateString('zh-TW') },
    { key: 'actions', header: '操作', render: (k: ApiKeyWithStats) => (
      <div className="flex gap-2">
        <Button size="sm" variant="secondary" onClick={() => openEditModal(k)}>編輯</Button>
        <Button size="sm" variant={k.is_active ? 'danger' : 'primary'} onClick={() => handleToggleStatus(k)}>{k.is_active ? '停用' : '啟用'}</Button>
      </div>
    )},
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">API Key 管理</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatsCard title="API Key 數量" value={apiKeys.length} icon="🔑" />
        <StatsCard title="總 Token 使用量" value={formatTokens(totalStats.total_tokens)} icon="📊" />
        <StatsCard title="總流量" value={formatBytes(totalStats.total_traffic_bytes)} icon="📡" />
      </div>

      {/* Create New Key Form */}
      <Card>
        <div className="p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">建立新 API Key</h2>
          <form onSubmit={handleCreate} className="flex gap-4 items-end">
            <div className="flex-1">
              <Input label="描述（選填）" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="例如：生產環境用" />
            </div>
            <Button type="submit">建立 API Key</Button>
          </form>
        </div>
      </Card>

      {/* New Key Display */}
      {newKeyValue && (
        <Card>
          <div className="p-4 bg-green-50 dark:bg-green-900/20">
            <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-2">新 API Key 已建立</h3>
            <p className="text-sm text-green-600 dark:text-green-400 mb-2">請立即複製此 Key，關閉後將無法再次查看完整內容。</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white dark:bg-gray-800 p-2 rounded border text-sm">{newKeyValue}</code>
              <Button onClick={() => copyToClipboard(newKeyValue)}>複製</Button>
              <Button variant="secondary" onClick={() => setNewKeyValue(null)}>關閉</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <div className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">搜尋</label>
              <input
                type="text"
                placeholder={isAdmin ? "搜尋 Key、描述、用戶名稱或 Email..." : "搜尋 Key 或描述..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="w-32">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">狀態</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">全部</option>
                <option value="active">啟用</option>
                <option value="inactive">停用</option>
              </select>
            </div>
            {(searchQuery || statusFilter !== 'all') && (
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
              >
                清除篩選
              </Button>
            )}
          </div>
          {filteredApiKeys.length !== apiKeys.length && (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              顯示 {filteredApiKeys.length} / {apiKeys.length} 個 API Key
            </p>
          )}
        </div>
      </Card>

      {/* API Keys List */}
      <Card>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <Table data={filteredApiKeys} columns={columns} keyExtractor={(k) => k.id} emptyMessage="暫無 API Key" />
        )}
      </Card>

      {/* Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="編輯 API Key">
        <form onSubmit={handleUpdate} className="space-y-4">
          <Input label="描述" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="例如：生產環境用" />
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>取消</Button>
            <Button type="submit">更新</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
